import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Validators, FormGroup, NonNullableFormBuilder } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { noop, Subject } from 'rxjs';
import { Store } from '@ngrx/store';
import { filter, map, take, takeUntil } from 'rxjs/operators';
import {
  BranchListItemResponse,
  CustomerMedicalInviteRequest,
  DepartmentListItemResponse,
  GroupListItemResponse,
  PermissionItemResponse,
  PspInviteRequest,
  UserItemResponse,
} from '../../../../../../api';
import { BaseApplication } from '../../../../../shared/enums/base-application.enum';
import { BaseAvatarSize } from '../../../../../shared/components/avatar/base-avatar.component';
import { BaseButtonSize } from '../../../../../shared/components/button/base-button.component';
import { BaseModalService } from '../../../../../shared/services/base-modal.service';
import { BaseRolesService } from '../../../../../core/services/base-roles.service';
import { BaseFilterInterface } from '../../../../../shared/interfaces/base-filter.interface';
import { baseEmailValidator } from '../../../../../shared/validators/base-email.validator';
import {
  baseAdministrationActions,
  baseAdministrationSelectors,
  BaseCoreState,
  baseSharedActions,
  baseSharedSelectors,
  baseUserActions,
  baseUserSelectors,
} from '../../../../../_store';
import { GenderType } from '../../../_models/base-user-profile.type';
import { InviteUserCustomerForm, InviteUserPspForm } from '../../../_interfaces/base-user-management.interface';
import { BaseRolesType } from '../../../../../core/constants/base-roles';
import { PERMISSION_GROUP_FILTERS } from '../../../_constans/permission-group-filters.const';
import { TranslateService } from '@ngx-translate/core';
import { BaseAdministrationHelpersService } from '../../../_services/base-administration-helpers.service';
import { BaseUserManagementService } from '../../../_services/base-user-management.service';

@Component({
  selector: 'base-user-management',
  templateUrl: './base-user-management.component.html',
  providers: [BaseUserManagementService],
})
export class BaseUserManagementComponent implements OnInit, OnDestroy {
  @Input() application: BaseApplication;
  @ViewChild('inviteUserModalTemplate') inviteUserModalTemplate: ElementRef;

  userList: UserItemResponse[] = [];
  filteredUserList: UserItemResponse[] = [];
  permissionGroups: GroupListItemResponse[] = [];
  pspBranches: BranchListItemResponse[] = [];
  customerDepartments: DepartmentListItemResponse[] = [];
  inviteUserCustomerForm: FormGroup<InviteUserCustomerForm>;
  inviteUserPspForm: FormGroup<InviteUserPspForm>;
  modalRef: NgbModalRef;
  permissions: PermissionItemResponse[];
  avatarSizes = BaseAvatarSize;
  userListLoaded = false;
  isPSP: boolean;
  isInviteUserFormSubmitted: boolean;
  activeUsersOnly = true;
  searchQuery: string;
  selectedFilter: BaseFilterInterface = PERMISSION_GROUP_FILTERS[0];
  usersNumberLabel: string;
  isUsersNumberLabelVisible: boolean;

  buttonSizes = BaseButtonSize;

  private destroy$ = new Subject<boolean>();

  constructor(
    private store: Store<BaseCoreState>,
    private modalService: BaseModalService,
    private fb: NonNullableFormBuilder,
    private rolesService: BaseRolesService,
    private router: Router,
    private route: ActivatedRoute,
    private translateService: TranslateService,
    private administrationHelpersService: BaseAdministrationHelpersService
  ) {}

  ngOnInit() {
    this.isPSP = this.rolesService.isPsp();
    this.initInviteUserForm();

    this.store
      .select(baseUserSelectors.getUserGroups)
      .pipe(
        takeUntil(this.destroy$),
        filter((state) => !!state.ok),
        map((state) => state.data as GroupListItemResponse[])
      )
      .subscribe((groups) => {
        this.permissionGroups = groups;
      });

    this.store
      .select(baseUserSelectors.getAdminAndUserList)
      .pipe(
        takeUntil(this.destroy$),
        filter((state) => !!state.ok),
        map((state) => state.data as UserItemResponse[])
      )
      .subscribe((users) => {
        this.userList = [...users].sort((a, b) =>
          a.position > b.position ? 1 : b.position > a.position || a.invited || b.invited ? -1 : 0
        );

        this.filterUsersByGroup(this.selectedFilter);
        this.userListLoaded = true;
      });

    this.store
      .select(baseAdministrationSelectors.getPermissions)
      .pipe(
        takeUntil(this.destroy$),
        filter((state) => !!state.ok),
        map((state) => state.data as (PermissionItemResponse & { isSelected?: boolean })[])
      )
      .subscribe((permissions) => {
        this.permissions = permissions;
      });

    this.store
      .select(baseSharedSelectors.getPspBranches)
      .pipe(
        takeUntil(this.destroy$),
        filter((state) => !!state.ok),
        map((state) => state.data as Array<BranchListItemResponse>)
      )
      .subscribe((branches) => (this.pspBranches = branches));

    this.store
      .select(baseUserSelectors.getUserInactiveFilter)
      .pipe(takeUntil(this.destroy$))
      .subscribe((activeUsersOnly) => (this.activeUsersOnly = activeUsersOnly));

    this.translateService.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.setUsersNumberLabelAndVisibility();
    });

    this.resetSearchQueryInUrl();
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  selectGroup(activeFilter: BaseFilterInterface): void {
    this.selectedFilter = activeFilter;
    this.filterUsersByGroup(activeFilter);
  }

  filterInactiveUsers(): void {
    this.activeUsersOnly = !this.activeUsersOnly;
    this.store.dispatch(baseUserActions.setUserInactiveFilter({ filterActive: this.activeUsersOnly }));
    this.loadUsersList();
  }

  openInviteUserModal(): void {
    if (this.isPSP) {
      this.store.dispatch(baseSharedActions.loadPspBranches({ payload: { isActive: true } }));

      this.inviteUserPspForm.patchValue({
        firstName: '',
        gender: null,
        lastName: '',
        email: '',
        group: undefined,
        pspBranches: undefined,
      });
    } else {
      this.inviteUserCustomerForm.patchValue({
        firstName: '',
        gender: null,
        lastName: '',
        email: '',
        group: undefined,
        customerDepartments: undefined,
        customerLocations: undefined,
        role: undefined,
      });
    }

    this.isInviteUserFormSubmitted = false;
    this.modalRef = this.modalService.open(
      {
        headText: 'user.invite',
        bodyTemplate: this.inviteUserModalTemplate,
        cancelButtonDisplayed: false,
        okButtonDisplayed: false,
        hideFooter: true,
      },
      {
        size: 'lg',
      }
    );
    this.modalRef.result.then(noop, noop);
  }

  confirmUserInvitation(): void {
    if (this.isPSP) {
      if (this.inviteUserPspForm.valid) {
        const inviteUserPspFormValue = this.inviteUserPspForm.getRawValue();

        const body: PspInviteRequest = {
          group: inviteUserPspFormValue.group?.id,
          gender: inviteUserPspFormValue.gender?.value || (1 as GenderType),
          email: inviteUserPspFormValue.email,
          firstName: inviteUserPspFormValue.firstName,
          lastName: inviteUserPspFormValue.lastName,
          pspBranches: inviteUserPspFormValue.pspBranches,
        };

        const payload = {
          fullList: true,
          body,
        };

        this.store.dispatch(baseAdministrationActions.inviteUser({ payload }));
        this.modalRef.close();
      }
    } else if (this.inviteUserCustomerForm.valid) {
      const inviteUserCustomerFormValue = this.inviteUserCustomerForm.getRawValue();

      const body: CustomerMedicalInviteRequest = {
        gender: inviteUserCustomerFormValue.gender?.value || (1 as GenderType),
        email: inviteUserCustomerFormValue.email,
        firstName: inviteUserCustomerFormValue.firstName,
        lastName: inviteUserCustomerFormValue.lastName,
        customerDepartments: inviteUserCustomerFormValue.customerDepartments?.map((dep) => dep.id),
        customerLocations: inviteUserCustomerFormValue.customerLocations?.map((loc) => loc.id),
        role: inviteUserCustomerFormValue.role,
        group: inviteUserCustomerFormValue?.group?.id,
      };

      const payload = {
        fullList: true,
        body,
      };
      this.store.dispatch(baseAdministrationActions.inviteUser({ payload }));
      this.modalRef.close();
    }
  }

  private filterUsersByGroup(activeFilter: BaseFilterInterface): void {
    this.filteredUserList = activeFilter.groupId
      ? this.userList.filter((item) => item.group?.id === activeFilter.groupId)
      : activeFilter.roles.length === 0
      ? [...this.userList]
      : this.userList.filter((item) => item.roles.some((item) => activeFilter.roles.includes(item as BaseRolesType)));

    this.setUsersNumberLabelAndVisibility();
  }

  private setUsersNumberLabelAndVisibility(): void {
    const itemsCount = this.filteredUserList.length;
    this.isUsersNumberLabelVisible = itemsCount > 0;

    if (!this.isUsersNumberLabelVisible) {
      return;
    }

    if (this.activeUsersOnly) {
      const entityName = itemsCount === 1 ? 'user.singular' : 'user.plural';

      this.usersNumberLabel = this.administrationHelpersService.setTotalEntitiesNumberLabel(itemsCount, entityName);
    } else {
      const activeItemsCount = this.filteredUserList.filter((user) => user.active).length;
      const inactiveItemsCount = this.filteredUserList.filter((user) => !user.active).length;

      this.usersNumberLabel = this.administrationHelpersService.setActiveInactiveEntitiesNumberLabel(
        activeItemsCount,
        inactiveItemsCount
      );
    }
  }

  private initInviteUserForm(): void {
    if (this.isPSP) {
      this.inviteUserPspForm = this.fb.group({
        firstName: ['', [Validators.required]],
        lastName: ['', [Validators.required]],
        email: ['', [Validators.required, baseEmailValidator()]],
        gender: [null as { label: string; value: GenderType } | null, [Validators.required]],
        group: [null as any, [Validators.required]],
        pspBranches: [null as any, [Validators.required]],
      });
    } else {
      this.inviteUserCustomerForm = this.fb.group({
        firstName: ['', [Validators.required]],
        lastName: ['', [Validators.required]],
        email: ['', [Validators.required, baseEmailValidator()]],
        gender: [null as { label: string; value: GenderType } | null, [Validators.required]],
        role: [null as any, [Validators.required]],
      });
    }
  }

  onSearchChange(value: string) {
    this.searchQuery = value.trim().toLowerCase();
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { search: this.searchQuery || null },
      queryParamsHandling: 'merge',
    });
    this.loadUsersList();
  }

  private loadUsersList(): void {
    this.store.dispatch(
      baseUserActions.loadAdminAndUserList({
        payload: {
          search: this.searchQuery?.length ? this.searchQuery.trim() : undefined,
        },
      })
    );
  }

  private resetSearchQueryInUrl(): void {
    this.route.queryParams.pipe(take(1)).subscribe((params) => {
      if (params.search && !this.searchQuery) {
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: { search: null },
          queryParamsHandling: 'merge',
        });
      }
    });
  }
}
